import { TaskProgressBar } from './TaskProgressBar';
import { useState, useEffect, useRef } from 'react';
import type { AgentTaskGroup } from '@/pages/canvas/canvas-types';

interface ChatToolProgressCardProps {
  groups: AgentTaskGroup[];
  isStreaming: boolean;
}

export function ChatToolProgressCard({ groups, isStreaming }: ChatToolProgressCardProps) {
  const [expanded, setExpanded] = useState(true);

  // Flatten steps across groups for a single progress list.
  const allSteps = groups.flatMap((g) => g.steps);
  const completedCount = allSteps.filter((s) => s.status === 'success').length;
  const totalSteps = allSteps.length;
  const isActive = isStreaming;
  const isDone = !isActive && totalSteps > 0 && completedCount === totalSteps;
  const hasFailure = allSteps.some((s) => s.status === 'error');
  const progressPercent = totalSteps > 0 ? Math.round((completedCount / totalSteps) * 100) : 0;

  // Label: use active group's label if running; "Working" while streaming without a group; "Completed N steps" when done.
  const activeGroup = groups.find((g) => g.status === 'active');
  const headerLabel = isDone
    ? `Completed ${completedCount} step${completedCount !== 1 ? 's' : ''}`
    : activeGroup?.label || groups[groups.length - 1]?.label || 'Working\u2026';

  // Auto-collapse on completion (like ThinkingTaskGroup does).
  const prevDoneRef = useRef(isDone);
  useEffect(() => {
    if (!prevDoneRef.current && isDone) setExpanded(false);
    prevDoneRef.current = isDone;
  }, [isDone]);

  if (groups.length === 0) return null;

  const dotColor = hasFailure ? '#EF4444' : isDone ? '#22c55e' : '#A04F6A';

  return (
    <div
      data-testid="chat-tool-progress-card"
      style={{
        background: '#eceef3',
        borderRadius: 24,
        padding: '20px 24px',
        marginBottom: 12,
        marginTop: 12,
      }}
    >
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: expanded ? 16 : 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: dotColor, flexShrink: 0, display: 'inline-block' }} />
          <span style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: 13, color: '#2e3338' }}>
            {headerLabel}
          </span>
        </div>
        <button
          onClick={() => setExpanded((v) => !v)}
          style={{
            marginLeft: 'auto',
            marginRight: 12,
            fontSize: 10,
            fontWeight: 700,
            color: '#A04F6A',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}
        >
          {expanded ? 'Hide process' : 'Show process'}
        </button>
        {totalSteps > 0 && (
          <span style={{ fontSize: 11, fontWeight: 700, color: '#5b5f65' }}>
            {completedCount} / {totalSteps} steps
          </span>
        )}
      </div>

      {expanded && totalSteps > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* Progress bar */}
          <TaskProgressBar percent={progressPercent} active={isActive} failed={hasFailure} />

          {/* Step list */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {allSteps.map((step) => {
              const isCompleted = step.status === 'success';
              const isRunning = step.status === 'running';
              const isFailed = step.status === 'error';
              return (
                <div key={step.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                  {isCompleted ? (
                    <span style={{ color: '#A04F6A', fontSize: 16, lineHeight: 1 }}>&#x25CF;</span>
                  ) : isRunning ? (
                    <span style={{ color: '#A04F6A', fontSize: 16, lineHeight: 1, opacity: 0.6 }}>&#x25D0;</span>
                  ) : isFailed ? (
                    <span style={{ color: '#EF4444', fontSize: 16, lineHeight: 1 }}>&#x2716;</span>
                  ) : (
                    <span style={{ color: '#aeb2b8', fontSize: 16, lineHeight: 1 }}>&#x25CB;</span>
                  )}
                  <span style={{
                    color: isCompleted ? '#2e3338' : isFailed ? '#EF4444' : '#5b5f65',
                    fontWeight: isRunning ? 600 : 400,
                    opacity: (!isCompleted && !isRunning && !isFailed) ? 0.6 : 1,
                    flex: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    {step.friendlyLabel ?? step.toolName}
                  </span>
                  {step.summary && (isCompleted || isFailed) && (
                    <span style={{ fontSize: 11, color: '#9CA3AF', flexShrink: 0 }}>
                      {step.summary.length > 50 ? step.summary.slice(0, 50) + '\u2026' : step.summary}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
